import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Select, Modal } from 'common/components';
import { deleteTwilioAccount } from 'services/api/PhoneNumberAPI';
import { Tenant } from 'types/accessManager.type';
import styles from '../PhoneNumberManager.module.scss';

interface DeleteAccountForm {
    tenantId: string;
}

interface DeleteTwilioAccountTabProps {
    tenants: Tenant[];
    isAccessManagerLoading: boolean;
    showNotification: (message: string, type: 'success' | 'error') => void;
}

const DeleteTwilioAccountTab: React.FC<DeleteTwilioAccountTabProps> = ({
    tenants,
    isAccessManagerLoading,
    showNotification,
}) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    const {
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm<DeleteAccountForm>({
        defaultValues: {
            tenantId: '',
        },
    });

    const selectedTenantId = watch('tenantId');

    const handleDeleteAccount = async () => {
        if (!selectedTenantId) return;

        setIsSubmitting(true);
        setShowDeleteModal(false);

        try {
            const response = await deleteTwilioAccount({ tenantId: selectedTenantId });

            showNotification(
                response.message ||
                    `Successfully deleted Twilio account. ${response.deletedNumbers} numbers deleted. Account closed: ${response.accountClosed}`,
                'success'
            );
            reset();
        } catch (error: any) {
            console.error('Error deleting Twilio account:', error);
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to delete Twilio account. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.deleteContainer}>
            <div className={styles.formSection}>
                <h2 className={styles.sectionTitle}>Delete Twilio Account</h2>
                <p className={styles.description}>
                    <strong>Warning:</strong> This will delete the Twilio sub-account for the selected tenant. This will
                    not delete the tenant itself, but will remove all phone numbers and close the Twilio account. This
                    action cannot be undone.
                </p>

                <div className={styles.formGroup}>
                    <Controller
                        name='tenantId'
                        control={control}
                        rules={{ required: 'Tenant is required' }}
                        render={({ field }) => (
                            <Select
                                label='Tenant *'
                                options={[
                                    { value: '', label: 'Select a Tenant', disabled: true },
                                    ...(tenants || []).map((tenant) => ({
                                        value: tenant.id,
                                        label: tenant.name,
                                    })),
                                ]}
                                {...field}
                                disabled={isAccessManagerLoading}
                            />
                        )}
                    />
                    {errors.tenantId && <span className={styles.errorText}>{errors.tenantId.message}</span>}
                </div>

                <div className={styles.buttonContainer}>
                    <Button
                        type='button'
                        variant='secondary'
                        disabled={!selectedTenantId || isSubmitting || isAccessManagerLoading}
                        onClick={() => setShowDeleteModal(true)}
                    >
                        Delete Twilio Account
                    </Button>
                </div>
            </div>

            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                size='sm'
                header='Delete Twilio Account'
                showDefaultFooter={false}
                footer={
                    <div className={styles.modalFooter}>
                        <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant='primary' onClick={handleDeleteAccount} disabled={isSubmitting}>
                            Delete Account
                        </Button>
                    </div>
                }
            >
                <p className={styles.modalMessage}>
                    Are you sure you want to delete this Twilio sub-account? This will not delete the tenant but will
                    delete all phone numbers and close the Twilio account. This action cannot be undone.
                </p>
            </Modal>
        </div>
    );
};

export default DeleteTwilioAccountTab;
